import { Alert, AlertTitle, Typography } from '@mui/material'

import { Box, LocationFields } from '@/components/ui'

interface LocationStepProps {
  /**
   * Set when the organization turned out to already have a location, which the
   * endpoint refuses to add a second one to. There is nothing left to collect, so
   * the fields give way to saying so.
   */
  alreadyExists?: boolean
  /**
   * Set when a location create has already failed for this organization. The
   * organization itself is saved at this point, which is the part the user needs
   * told plainly.
   */
  failureMessage?: string
  organizationName: string
  /** Whether the organization predates this run, as a resumed setup's does. */
  organizationOnRecord: boolean
}

export const LocationStep = ({
  alreadyExists,
  failureMessage,
  organizationName,
  organizationOnRecord,
}: LocationStepProps) => {
  if (alreadyExists) {
    return (
      <Alert severity='info'>
        <AlertTitle>{organizationName} already has its location</AlertTitle>
        Most likely an earlier attempt here saved it and its response never arrived.
        Nothing more is needed: an organization keeps the first location it is given,
        and it cannot be amended from this tool.
      </Alert>
    )
  }

  return (
    <Box variant='flexColumn' gap='md'>
      {failureMessage && (
        <Alert severity='error'>
          <AlertTitle>
            {organizationOnRecord
              ? `${organizationName} still has no location`
              : `${organizationName} was created, but its location was not`}
          </AlertTitle>
          {failureMessage} The organization is saved and listed in the directory — correct
          the location below and try again.
        </Alert>
      )}
      <Typography variant='body2' color='text.secondary'>
        The organization has no address, phone, or time zone of its own, so this location
        is where they live. It becomes the organization&rsquo;s primary location, and the
        practice manages any further locations from its own settings.
      </Typography>
      <LocationFields />
    </Box>
  )
}
